import { useIntl } from 'react-intl'
import { getSumExtraGuestsSurcharge } from 'lib/checkout/cartReservationUtils'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import { useMemo } from 'react'
import useDefaultAirport from 'hooks/useDefaultAirport'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import TextButton from 'LuxKit/Button/TextButton'
import config from 'constants/config'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import { useOfferPriceWithMember } from 'hooks/Offers/useOfferPrice'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { connect } from 'react-redux'
import { EmptyArray, unique } from 'lib/array/arrayUtils'
import OfferPriceDetailsRow from 'components/Common/PriceDetails/OfferPriceDetailsRow'
import { useDirectSearchPrices } from 'hooks/Search/useSearchPrices'
import offerPageURL from 'lib/offer/offerPageURL'
import { useAppSelector } from 'hooks/reduxHooks'
import { getIsMultiRoomEnabled } from 'selectors/featuresSelectors'
import useSearchBundledBurnPoints from 'hooks/useSearchBundledBurnPoints'
import useSearchBurnEnabled from 'hooks/useSearchBurnEnabled'
import getLoyaltyProductType from 'loyalty/lib/getLoyaltyProductType'
import getHotelEncryptedMargin from 'loyalty/lib/margin/getHotelEncryptedMargin'
import LoyaltyBurnPriceDisplay from 'loyalty/components/LoyaltyBurnPriceDisplay'
import getCharterBundleHotelOnlyDiscount from 'lib/offer/getCharterBundleHotelOnlyDiscount'
import useHotelSearchPriceAlignment from 'hooks/Experiments/useHotelSearchPriceAlignment'
import styled from 'styled-components'
import clsx from 'clsx'

const PricingContainer = styled.div`
  &.align-end {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    text-align: end;
  }
`

interface MappedProps {
  canViewLuxPlusBenefits: boolean
}

interface Props extends MappedProps {
  offer: App.Offer | App.OfferSummary
  filters?: App.OfferListFilters
  rooms?: Array<App.Occupants>
  pkg: App.Package
  pricing?: App.OfferAvailableRate
  hasDates?: boolean
  offerUrl?: string
}

function SearchTileFlightOnlyPricing(props: Props) {
  const {
    offer,
    pkg,
    pricing,
    hasDates,
    rooms = EmptyArray,
    offerUrl,
    canViewLuxPlusBenefits,
    filters,
  }: Props = props

  const intl = useIntl()
  const alignWithPriceBlockExperiment = useHotelSearchPriceAlignment()
  const isMultiRoomEnable = useAppSelector(state => getIsMultiRoomEnabled(state, offer))

  const defaultAirport = useDefaultAirport()
  const flightPrice = (offer.flightPrices[defaultAirport?.code ?? ''] || 0)

  const directSearchPrices = useDirectSearchPrices({ filters: filters ?? {}, offerId: offer.id })

  const priceMultiplier = rooms?.length || 1
  const extraGuestSurcharges = useMemo(() => getSumExtraGuestsSurcharge(rooms || [], offer, pkg), [pkg, rooms, offer])

  const {
    price = directSearchPrices?.lowestPrice ?? 0,
    memberPrice = directSearchPrices?.lowestMemberPrice ?? 0,
    taxesAndFees = 0,
    propertyFees = 0,
  } = pricing ?? pkg

  const showPriceDetails = taxesAndFees > 0 || propertyFees > 0 || config.agentHub.isEnabled
  let hotelPrice = price * priceMultiplier + extraGuestSurcharges + flightPrice
  const showMemberPrice = memberPrice > 0
  const rateOrPkgMemberPrice = pricing?.memberPriceWithSurcharge ?? pkg.memberPrice
  const hotelMemberPrice = showMemberPrice ? rateOrPkgMemberPrice * priceMultiplier + extraGuestSurcharges + flightPrice : 0
  let totalPrice = hotelPrice + propertyFees
  let totalMemberPrice = showMemberPrice ? hotelMemberPrice + propertyFees : 0
  const pkgValueOrMemberValue = canViewLuxPlusBenefits ? pkg.memberValue : pkg.value
  let totalValue = pkgValueOrMemberValue * priceMultiplier + extraGuestSurcharges + flightPrice
  let shouldShowValue = pkg.shouldDisplayValue && pkgValueOrMemberValue > 0 && totalValue >= (showMemberPrice ? totalMemberPrice : totalPrice)

  if (directSearchPrices) {
    // search does not know anything about flight prices, so we just add them in to our pricing
    hotelPrice = (directSearchPrices.lowestPrice ?? 0) + flightPrice
    totalPrice = hotelPrice // we already include property fees in the direct search prices
    totalMemberPrice = showMemberPrice ? (directSearchPrices.lowestMemberPrice ?? 0) + flightPrice : 0
    totalValue = (directSearchPrices.lowestPriceValue ?? 0) + flightPrice
    shouldShowValue = pkg.shouldDisplayValue && pkgValueOrMemberValue > 0 && totalValue >= (showMemberPrice ? totalMemberPrice : totalPrice)
  }

  // Maldives charter bundles: the flight leg has no discount of its own, so show the
  // hotel-only discount (from lowestPricePackage) instead of the flight-diluted bundle %.
  const { discountPercent: charterHotelOnlyDiscountPercent, memberDiscountPercent: charterHotelOnlyMemberDiscountPercent } = getCharterBundleHotelOnlyDiscount(offer)
  const valueOffPercent = charterHotelOnlyDiscountPercent !== undefined
    ? (showMemberPrice ? charterHotelOnlyMemberDiscountPercent ?? charterHotelOnlyDiscountPercent : charterHotelOnlyDiscountPercent)
    : getValueOffPercent(totalValue, showMemberPrice ? hotelMemberPrice : hotelPrice)

  // https://aussiecommerce.atlassian.net/browse/CONLT-863
  // Only show discount pill for forced bundles
  const shouldShowRate = !offer.forceBundleId && offer.bundledWithFlightsOnly && valueOffPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE

  const {
    total: displayPrice,
    memberTotal: displayMemberPrice,
    saleUnit: displaySaleUnit,
  } = useOfferPriceWithMember({
    value: undefined,
    saleUnit: 'total',
    duration: pkg.duration,
    total: totalPrice,
    memberTotal: totalMemberPrice,
  })

  const priceCaption = useMemo(() => {
    const roomCount = rooms.length
    const showRooms = isMultiRoomEnable && roomCount > 1
    const hasFlightPrice = flightPrice > 0
    const occupants = formatOccupantsShort(rooms)
    const roomsText = showRooms ? intl.formatMessage(
      {
        id: 'gO-zXbm8OKPKBjHWPUy4',
        defaultMessage: '{count, plural, one {# room} other {# rooms}}',
        description: 'Room count for flight search tile pricing in the flights flow',
      },
      { count: roomCount },
    ) : ''

    const formatNightsCaption = (nightCount: number) => {
      if (hasFlightPrice) {
        if (showRooms) {
          return intl.formatMessage(
            {
              id: '5NZ5Lj1HH5NpdTl2l0ts',
              defaultMessage: '{nights, plural, one {# night} other {# nights}}, {roomsText} & flights, {occupants}',
              description: 'Duration with rooms, flights, and occupants for flight search tile pricing',
            },
            { nights: nightCount, roomsText, occupants },
          )
        }
        return intl.formatMessage(
          {
            id: 'q0nAkrKOcM75aFfoJG8F',
            defaultMessage: '{nights, plural, one {# night} other {# nights}} & flights, {occupants}',
            description: 'Duration with flights and occupants for flight search tile pricing',
          },
          { nights: nightCount, occupants },
        )
      }
      if (showRooms) {
        return intl.formatMessage(
          {
            id: 'X9wL-rfyDzbRr8CIcVlE',
            defaultMessage: '{nights, plural, one {# night} other {# nights}}, {roomsText}, hotel only, {occupants}',
            description: 'Duration with rooms, hotel only, and occupants for flight search tile pricing',
          },
          { nights: nightCount, roomsText, occupants },
        )
      }
      return intl.formatMessage(
        {
          id: '5REgnMfJ3x_bIumqL3fY',
          defaultMessage: '{nights, plural, one {# night} other {# nights}}, hotel only, {occupants}',
          description: 'Duration with hotel only and occupants for flight search tile pricing',
        },
        { nights: nightCount, occupants },
      )
    }

    if (directSearchPrices?.duration) {
      return formatNightsCaption(directSearchPrices.duration)
    }
    if (hasDates) {
      if (showRooms) {
        return intl.formatMessage(
          {
            id: '-dULCQg4gVIVG7YNwEzx',
            defaultMessage: '{nights, plural, one {# night} other {# nights}}, {roomsText} & flights, {occupants}',
            description: 'Duration with rooms, flights, and occupants for flight search tile with dates',
          },
          { nights: pkg.duration, roomsText, occupants },
        )
      }
      return intl.formatMessage(
        {
          id: '67KGlYckv51WGA4CshAj',
          defaultMessage: '{nights, plural, one {# night} other {# nights}} & flights, {occupants}',
          description: 'Duration with flights and occupants for flight search tile with dates',
        },
        { nights: pkg.duration, occupants },
      )
    }
    if (offer.tileDurationLabel) {
      if (showRooms) {
        return intl.formatMessage(
          {
            id: 'NLaj8defMkcIbUV0TZeC',
            defaultMessage: '{label}, {roomsText} & flights from',
            description: 'Duration label with rooms and flights for flight search tile pricing',
          },
          { label: offer.tileDurationLabel.toLocaleLowerCase(), roomsText },
        )
      }
      return intl.formatMessage(
        {
          id: '9n0ThZv69oN2NJdc7SEX',
          defaultMessage: '{label} & flights from',
          description: 'Duration label with flights for flight search tile pricing',
        },
        { label: offer.tileDurationLabel.toLocaleLowerCase() },
      )
    }
    return intl.formatMessage({
      id: 'dDu3dw_T_j9T2ZwE-4Ze',
      defaultMessage: 'Hotel & flights from',
      description: 'Fallback price caption for flight search tile pricing',
    })
  }, [rooms, isMultiRoomEnable, directSearchPrices?.duration, hasDates, pkg.duration, offer.tileDurationLabel, flightPrice, intl])

  const offerLocations = useMemo(() => unique(offer.locations.concat(offer.location)),
    [offer.locations, offer.location])
  const vendorName = offer.vendorName

  const burnSaleUnit = (displaySaleUnit === 'night' || displaySaleUnit === 'person') ? displaySaleUnit : 'total'
  const { hasSelectedBurnToggle } = useSearchBurnEnabled()
  const viewPriceInPoints = !!filters?.viewPriceInPoints && !hasSelectedBurnToggle
  const burnResult = useSearchBundledBurnPoints(displayPrice, getLoyaltyProductType(offer) ?? 'hotel_flash', flightPrice, getHotelEncryptedMargin(offer.type, pkg), { viewPriceInPoints })

  let priceDisplay: React.ReactNode
  if (burnResult.enabled) {
    priceDisplay = <LoyaltyBurnPriceDisplay
      horizontalAlign={alignWithPriceBlockExperiment ? 'end' : undefined}
      totalPrice={displayPrice}
      pointsToBurn={burnResult.pointsToBurn}
      priceInPoints={burnResult.priceInPoints}
      memberPrice={displayMemberPrice}
      size="L"
      saleUnit={burnSaleUnit}
      hideZeroCash={burnResult.hideZeroCash}
    />
  } else {
    priceDisplay = <LuxPlusPriceStack
      horizontalAlign={alignWithPriceBlockExperiment ? 'end' : undefined}
      price={displayPrice}
      memberPrice={displayMemberPrice}
      size="L"
      saleUnit={displaySaleUnit}
      testid="search-price"
      offerConfig={offer.luxPlus}
    />
  }

  return <PricingContainer className={clsx({ 'align-end': alignWithPriceBlockExperiment })}>
    <PriceRowPriceCaption>
      {priceCaption}
    </PriceRowPriceCaption>
    {priceDisplay}
    <PriceRowTaxesAndFees />
    {shouldShowValue && <PriceRowValueDiscountWithCaption
      data-testid="value-box"
      size="M"
      originalValue={totalValue}
      discountPercentage={shouldShowRate ? valueOffPercent : undefined}
    />}
    <PriceRowAgentHubCommission
      productType={offer.productType}
      offerId={offer.id}
      vendorName={vendorName}
      offerLocations={offerLocations}
      packageId={pkg.id}
    />
    {showPriceDetails && <OfferPriceDetailsRow
      trigger="price-row"
      triggerSize="M"
      offer={offer}
      duration={pkg.duration}
      propertyFees={propertyFees}
      hotelPrice={hotelPrice}
      rooms={rooms}
      dueAtPropertyMessage={offer.property?.taxesAndFeesContent ?? ''}
      hotelMemberPrice={hotelMemberPrice}
      showMemberPrice={showMemberPrice}
      context="search"
      cta={
        <TextButton kind="primary" fit="flex" to={offerUrl ?? offerPageURL(offer)}>
          {intl.formatMessage({
            id: 'HXYYtZKv2mekm6wgIuOU',
            defaultMessage: 'View Offer',
            description: 'CTA button in flight pricing details to view the full offer',
          })}
        </TextButton>
      }
    />}
  </PricingContainer>
}

const mapStateToProps = (state: App.State): MappedProps => ({
  canViewLuxPlusBenefits: getCanViewLuxPlusBenefits(state),
})

export default connect(mapStateToProps)(SearchTileFlightOnlyPricing)
